import { useState, useRef, useEffect, useCallback } from 'react'
import { findImageByReferenceName, getImageReferenceName } from '../../utils/imageMentions'
import './SolutionTextareaWithMentions.css'

function SolutionTextareaWithMentions ({
  value,
  onChange,
  images = [],
  placeholder,
  rows = 12,
  disabled = false,
  required = false
}) {
  const textareaRef = useRef(null)
  const dropdownRef = useRef(null)
  const [showMentions, setShowMentions] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [cursorPosition, setCursorPosition] = useState(0)

  // ============================================================================
  // VERBESSERTE LISTEN-LOGIK
  // ============================================================================

  /**
   * Parse list prefix from a line
   * Returns: { indent: '  ', number: '1.2.3', fullPrefix: '  1.2.3. ' } or null
   */
  const parseListPrefix = (line) => {
    // Match numbered list: "  1.2.3. "
    const numberedMatch = line.match(/^(\s*)(\d+(?:\.\d+)*)\.\s/)
    if (numberedMatch) {
      return {
        type: 'numbered',
        indent: numberedMatch[1],
        number: numberedMatch[2],
        fullPrefix: numberedMatch[0]
      }
    }
    
    // Match bullet list: "  - " or "  * "
    const bulletMatch = line.match(/^(\s*)([-*])\s/)
    if (bulletMatch) {
      return {
        type: 'bullet',
        indent: bulletMatch[1],
        bullet: bulletMatch[2],
        fullPrefix: bulletMatch[0]
      }
    }
    
    return null
  }

  /**
   * Get the depth level of a number (e.g., "1" = 0, "1.2" = 1, "1.2.3" = 2)
   */
  const getNumberDepth = (number) => {
    return number.split('.').length - 1
  }

  /**
   * Increment a list number at the same level
   * "1" → "2", "1.2" → "1.3", "1.2.3" → "1.2.4"
   */
  const incrementNumber = (number) => {
    const parts = number.split('.')
    const lastPart = parseInt(parts[parts.length - 1])
    parts[parts.length - 1] = String(lastPart + 1)
    return parts.join('.')
  }

  /**
   * Create a nested number from a parent
   * "1" → "1.1", "1.2" → "1.2.1"
   */
  const nestNumber = (parentNumber) => {
    return `${parentNumber}.1`
  }

  /**
   * Un-nest a number (go up one level)
   * "1.2.3" → "2", "1.2" → "2", "1" → null
   */
  const unnestNumber = (number) => {
    const parts = number.split('.')
    if (parts.length === 1) {
      // Top level - can't un-nest
      return null
    }
    
    // Get parent parts and increment the last one
    const parentParts = parts.slice(0, -1)
    const lastPart = parseInt(parentParts[parentParts.length - 1])
    parentParts[parentParts.length - 1] = String(lastPart + 1)
    
    return parentParts.join('.')
  }

  /**
   * Find the last numbered list item before the current line to determine next number
   */
  const findPreviousNumber = (lines, currentLineIndex) => {
    const currentPrefix = parseListPrefix(lines[currentLineIndex])
    if (!currentPrefix || currentPrefix.type !== 'numbered') return null
    
    const currentIndentLevel = currentPrefix.indent.length
    
    // Look backwards for a numbered list at the same indent level
    for (let i = currentLineIndex - 1; i >= 0; i--) {
      const prefix = parseListPrefix(lines[i])
      if (!prefix || prefix.type !== 'numbered') continue
      
      if (prefix.indent.length === currentIndentLevel) {
        return prefix.number
      }
      
      // Stop if we hit a less indented line
      if (prefix.indent.length < currentIndentLevel) {
        break
      }
    }
    
    return null
  }

  /**
   * Check if line is empty except for list prefix
   */
  const isListLineEmpty = (line) => {
    const prefix = parseListPrefix(line)
    if (!prefix) return false
    return line.trim() === prefix.fullPrefix.trim()
  }

  // ============================================================================
  // MENTION LOGIC (UNVERÄNDERT)
  // ============================================================================

  // Filter images based on query
  const filteredImages = images.filter(img => {
    if (!mentionQuery) return true
    
    const refName = img.referenceName || getImageReferenceName(img.filename || img.name || '')
    return refName.toLowerCase().includes(mentionQuery.toLowerCase())
  })

  // Calculate mention position relative to cursor
  const updateMentionPosition = useCallback(() => {
    if (!textareaRef.current) {
      return
    }

    const textarea = textareaRef.current
    const textBeforeCursor = value.substring(0, cursorPosition)
    
    const lastAtIndex = textBeforeCursor.lastIndexOf('@')
    
    if (lastAtIndex === -1) {
      setShowMentions(false)
      return
    }

    const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1)
    
    if (/\s/.test(textAfterAt)) {
      setShowMentions(false)
      return
    }
    
    const computedStyle = window.getComputedStyle(textarea)
    const container = textarea.parentElement
    if (!container) {
      return
    }
    
    const lines = textBeforeCursor.split('\n')
    const currentLineIndex = lines.length - 1
    const currentLineText = lines[currentLineIndex]
    
    const textBeforeAtInLine = currentLineText.substring(0, currentLineText.lastIndexOf('@'))
    
    const mirrorDiv = document.createElement('div')
    const stylesToCopy = [
      'font', 'fontSize', 'fontFamily', 'fontWeight', 'fontStyle',
      'letterSpacing', 'textTransform', 'padding', 'border',
      'borderLeftWidth', 'borderRightWidth', 'borderTopWidth', 'borderBottomWidth',
      'boxSizing', 'width', 'lineHeight', 'wordSpacing', 'textIndent'
    ]
    
    stylesToCopy.forEach(prop => {
      mirrorDiv.style[prop] = computedStyle[prop]
    })
    
    mirrorDiv.style.position = 'absolute'
    mirrorDiv.style.visibility = 'hidden'
    mirrorDiv.style.whiteSpace = 'pre-wrap'
    mirrorDiv.style.wordWrap = 'break-word'
    mirrorDiv.style.top = '0'
    mirrorDiv.style.left = '0'
    mirrorDiv.style.height = 'auto'
    mirrorDiv.style.overflow = 'hidden'
    
    const textUpToAt = lines.slice(0, -1).join('\n') + (lines.length > 1 ? '\n' : '') + textBeforeAtInLine
    mirrorDiv.textContent = textUpToAt
    
    container.appendChild(mirrorDiv)
    
    const measureSpan = document.createElement('span')
    measureSpan.style.position = 'absolute'
    measureSpan.style.visibility = 'hidden'
    measureSpan.style.whiteSpace = 'pre'
    measureSpan.style.font = computedStyle.font
    measureSpan.style.fontSize = computedStyle.fontSize
    measureSpan.style.fontFamily = computedStyle.fontFamily
    measureSpan.style.fontWeight = computedStyle.fontWeight
    measureSpan.style.fontStyle = computedStyle.fontStyle
    measureSpan.style.letterSpacing = computedStyle.letterSpacing
    measureSpan.textContent = textBeforeAtInLine
    document.body.appendChild(measureSpan)
    const measureWidth = measureSpan.offsetWidth
    document.body.removeChild(measureSpan)
    
    const textareaRect = textarea.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    
    container.removeChild(mirrorDiv)
    
    const scrollTop = textarea.scrollTop
    const scrollLeft = textarea.scrollLeft
    const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.6
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0
    
    const top = (currentLineIndex * lineHeight) + paddingTop - scrollTop + lineHeight
    const left = measureWidth + paddingLeft - scrollLeft
    
    setMentionPosition({ top, left })
  }, [value, cursorPosition])

  useEffect(() => {
    if (showMentions) {
      updateMentionPosition()
    }
  }, [showMentions, updateMentionPosition])

  // Handle input changes
  const handleInput = (e) => {
    const newValue = e.target.value
    const newCursorPos = e.target.selectionStart
    
    onChange(newValue)
    setCursorPosition(newCursorPos)
    
    const textBeforeCursor = newValue.substring(0, newCursorPos)
    const lastAtIndex = textBeforeCursor.lastIndexOf('@')
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1)
      
      if (!/\s/.test(textAfterAt)) {
        setMentionQuery(textAfterAt)
        setShowMentions(true)
        setSelectedIndex(0)
      } else {
        setShowMentions(false)
      }
    } else {
      setShowMentions(false)
    }
  }

  // ============================================================================
  // VERBESSERTE KEYBOARD HANDLING
  // ============================================================================

  const handleKeyDown = (e) => {
    // Handle mentions first (höchste Priorität)
    if (showMentions) {
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        if (filteredImages.length > 0) {
          insertMention(filteredImages[selectedIndex])
        }
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(prev => 
          prev < filteredImages.length - 1 ? prev + 1 : 0
        )
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredImages.length - 1
        )
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setShowMentions(false)
        return
      }
    }

    const textarea = textareaRef.current
    if (!textarea) return

    const cursorPos = textarea.selectionStart
    const textBeforeCursor = value.substring(0, cursorPos)
    const textAfterCursor = value.substring(cursorPos)
    const lines = value.split('\n')
    
    // Find current line
    let currentLineIndex = 0
    let charCount = 0
    for (let i = 0; i < lines.length; i++) {
      if (charCount + lines[i].length >= cursorPos) {
        currentLineIndex = i
        break
      }
      charCount += lines[i].length + 1 // +1 for newline
    }
    
    const currentLine = lines[currentLineIndex]
    const posInLine = cursorPos - charCount

    // ========================================================================
    // ENTER: Neue Zeile mit nächster Nummer
    // ========================================================================
    if (e.key === 'Enter') {
      const prefix = parseListPrefix(currentLine)
      
      if (prefix) {
        e.preventDefault()
        
        // Wenn Zeile leer ist (nur Präfix), Liste beenden
        if (isListLineEmpty(currentLine)) {
          const newText = lines.slice(0, currentLineIndex).join('\n') + 
                         (currentLineIndex > 0 ? '\n' : '') + 
                         '\n' + 
                         lines.slice(currentLineIndex + 1).join('\n')
          const newCursorPos = charCount
          
          onChange(newText)
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.setSelectionRange(newCursorPos, newCursorPos)
            }
            setCursorPosition(newCursorPos)
          }, 0)
          return
        }
        
        // Neue Zeile mit nächster Nummer erstellen
        if (prefix.type === 'numbered') {
          const nextNumber = incrementNumber(prefix.number)
          const newLinePrefix = prefix.indent + nextNumber + '. '
          
          const textBeforeInLine = currentLine.substring(0, posInLine)
          const textAfterInLine = currentLine.substring(posInLine)
          
          const newText = lines.slice(0, currentLineIndex).join('\n') +
                         (currentLineIndex > 0 ? '\n' : '') +
                         textBeforeInLine + '\n' +
                         newLinePrefix +
                         textAfterInLine +
                         (currentLineIndex < lines.length - 1 ? '\n' : '') +
                         lines.slice(currentLineIndex + 1).join('\n')
          
          const newCursorPos = charCount + textBeforeInLine.length + 1 + newLinePrefix.length
          
          onChange(newText)
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.setSelectionRange(newCursorPos, newCursorPos)
            }
            setCursorPosition(newCursorPos)
          }, 0)
          return
        }
        
        // Bullet list
        if (prefix.type === 'bullet') {
          const newLinePrefix = prefix.indent + prefix.bullet + ' '
          
          const textBeforeInLine = currentLine.substring(0, posInLine)
          const textAfterInLine = currentLine.substring(posInLine)
          
          const newText = lines.slice(0, currentLineIndex).join('\n') +
                         (currentLineIndex > 0 ? '\n' : '') +
                         textBeforeInLine + '\n' +
                         newLinePrefix +
                         textAfterInLine +
                         (currentLineIndex < lines.length - 1 ? '\n' : '') +
                         lines.slice(currentLineIndex + 1).join('\n')
          
          const newCursorPos = charCount + textBeforeInLine.length + 1 + newLinePrefix.length
          
          onChange(newText)
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.setSelectionRange(newCursorPos, newCursorPos)
            }
            setCursorPosition(newCursorPos)
          }, 0)
          return
        }
      }
    }

    // ========================================================================
    // TAB: Einrückung (nimm vorherige Zeile als Parent)
    // ========================================================================
    if (e.key === 'Tab') {
      const prefix = parseListPrefix(currentLine)
      
      if (prefix && prefix.type === 'numbered') {
        e.preventDefault()
        
        // Suche die direkt vorherige nummerierte Zeile (egal welcher Indent)
        let parentNumber = null
        
        for (let i = currentLineIndex - 1; i >= 0; i--) {
          const prevPrefix = parseListPrefix(lines[i])
          if (prevPrefix && prevPrefix.type === 'numbered') {
            // Nimm diese Nummer als Parent
            parentNumber = prevPrefix.number
            break
          }
        }
        
        // Wenn keine vorherige Nummer gefunden, mache es zu "1.1"
        if (!parentNumber) {
          parentNumber = '1'
        }
        
        const nestedNumber = nestNumber(parentNumber)
        const newIndent = prefix.indent + '  ' // 2 spaces
        const newPrefix = newIndent + nestedNumber + '. '
        
        const textAfterPrefix = currentLine.substring(prefix.fullPrefix.length)
        const newLine = newPrefix + textAfterPrefix
        
        const newText = lines.slice(0, currentLineIndex).join('\n') +
                       (currentLineIndex > 0 ? '\n' : '') +
                       newLine +
                       (currentLineIndex < lines.length - 1 ? '\n' : '') +
                       lines.slice(currentLineIndex + 1).join('\n')
        
        // Cursor Position anpassen
        const oldPrefixLength = prefix.fullPrefix.length
        const newPrefixLength = newPrefix.length
        const newCursorPos = cursorPos + (newPrefixLength - oldPrefixLength)
        
        onChange(newText)
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.setSelectionRange(newCursorPos, newCursorPos)
          }
          setCursorPosition(newCursorPos)
        }, 0)
        return
      }
    }

    // ========================================================================
    // BACKSPACE: Intelligentes Löschen
    // ========================================================================
    if (e.key === 'Backspace') {
      const prefix = parseListPrefix(currentLine)
      
      // Prüfen ob Cursor direkt nach dem Präfix steht
      if (prefix && posInLine === prefix.fullPrefix.length) {
        e.preventDefault()
        
        const textAfterPrefix = currentLine.substring(prefix.fullPrefix.length)
        
        if (prefix.type === 'numbered') {
          const depth = getNumberDepth(prefix.number)
          
          // Nested number (z.B. "1.1" oder "1.2.3")
          if (depth > 0) {
            const unnested = unnestNumber(prefix.number)
            
            if (unnested) {
              // Un-nest: "1.1" → "2."
              const newIndent = prefix.indent.length >= 2 
                ? prefix.indent.substring(2) 
                : ''
              const newPrefix = newIndent + unnested + '. '
              const newLine = newPrefix + textAfterPrefix
              
              const newText = lines.slice(0, currentLineIndex).join('\n') +
                             (currentLineIndex > 0 ? '\n' : '') +
                             newLine +
                             (currentLineIndex < lines.length - 1 ? '\n' : '') +
                             lines.slice(currentLineIndex + 1).join('\n')
              
              const newCursorPos = charCount + newPrefix.length
              
              onChange(newText)
              setTimeout(() => {
                if (textareaRef.current) {
                  textareaRef.current.setSelectionRange(newCursorPos, newCursorPos)
                }
                setCursorPosition(newCursorPos)
              }, 0)
              return
            }
          }
          
          // Top-level number (z.B. "1." oder "2.")
          // Präfix komplett entfernen
          const newLine = prefix.indent + textAfterPrefix
          const newText = lines.slice(0, currentLineIndex).join('\n') +
                         (currentLineIndex > 0 ? '\n' : '') +
                         newLine +
                         (currentLineIndex < lines.length - 1 ? '\n' : '') +
                         lines.slice(currentLineIndex + 1).join('\n')
          
          const newCursorPos = charCount + prefix.indent.length
          
          onChange(newText)
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.setSelectionRange(newCursorPos, newCursorPos)
            }
            setCursorPosition(newCursorPos)
          }, 0)
          return
        }
        
        // Bullet list - Präfix entfernen
        if (prefix.type === 'bullet') {
          const newLine = prefix.indent + textAfterPrefix
          const newText = lines.slice(0, currentLineIndex).join('\n') +
                         (currentLineIndex > 0 ? '\n' : '') +
                         newLine +
                         (currentLineIndex < lines.length - 1 ? '\n' : '') +
                         lines.slice(currentLineIndex + 1).join('\n')
          
          const newCursorPos = charCount + prefix.indent.length
          
          onChange(newText)
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.setSelectionRange(newCursorPos, newCursorPos)
            }
            setCursorPosition(newCursorPos)
          }, 0)
          return
        }
      }
    }
  }

  // ============================================================================
  // MENTION INSERT (UNVERÄNDERT)
  // ============================================================================

  const insertMention = (image) => {
    if (!textareaRef.current) return

    const refName = image.referenceName || getImageReferenceName(image.filename || image.name || '')
    const textBeforeCursor = value.substring(0, cursorPosition)
    const textAfterCursor = value.substring(cursorPosition)
    
    const lastAtIndex = textBeforeCursor.lastIndexOf('@')
    
    if (lastAtIndex === -1) return

    const newText = 
      value.substring(0, lastAtIndex) + 
      `@${refName}` + 
      textAfterCursor

    const newCursorPos = lastAtIndex + refName.length + 1
    
    onChange(newText)
    setShowMentions(false)
    
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos)
        textareaRef.current.focus()
      }
      setCursorPosition(newCursorPos)
    }, 0)
  }

  const handleMentionClick = (image) => {
    insertMention(image)
  }

  const handleSelectionChange = () => {
    if (textareaRef.current) {
      setCursorPosition(textareaRef.current.selectionStart)
    }
  }

  // Close mentions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target)
      ) {
        setShowMentions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Update cursor position when value changes externally
  useEffect(() => {
    if (textareaRef.current) {
      const pos = textareaRef.current.selectionStart
      setCursorPosition(pos)
    }
  }, [value])

  return (
    <div className='solution-textarea-with-mentions'>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        onSelect={handleSelectionChange}
        onFocus={handleSelectionChange}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        required={required}
        className='solution-textarea'
      />
      
      {showMentions && filteredImages.length > 0 && (
        <div
          ref={dropdownRef}
          className='mention-dropdown'
          style={{
            position: 'absolute',
            top: `${mentionPosition.top}px`,
            left: `${mentionPosition.left}px`,
            zIndex: 1000
          }}
        >
          {filteredImages.map((image, index) => {
            const refName = image.referenceName || getImageReferenceName(image.filename || image.name || '')
            const isVideo = image.mediaType === 'video' || 
                          (image.preview || image.url || '').startsWith('data:video/') || 
                          (image.filename || image.name || '').match(/\.(mp4|webm|mov)$/i)
            
            return (
              <div
                key={image.id || index}
                className={`mention-option ${index === selectedIndex ? 'selected' : ''}`}
                onClick={() => handleMentionClick(image)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {isVideo ? (
                  <video
                    src={image.preview || image.url}
                    className='mention-thumbnail'
                    preload="metadata"
                    muted
                    playsInline
                    style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e5e7eb' }}
                  />
                ) : (
                  <img
                    src={image.preview || image.url}
                    alt={refName}
                    className='mention-thumbnail'
                  />
                )}
                <span className='mention-name'>@{refName}</span>
              </div>
            )
          })}
        </div>
      )}
      
      {images.length > 0 && (
        <div className='mention-hint'>
          💡 Tipp: Tippen Sie <strong>@</strong> um ein Bild/Video zu referenzieren
        </div>
      )}
    </div>
  )
}

export default SolutionTextareaWithMentions